/**
 * 會員資料頁（#1 #2）
 *
 * 顯示：基本資料、會籍期間、等級
 * 操作：更新姓名 / Email / 生日。手機只能在補資料頁首次填寫;留過手機後姓名也鎖定
 *       (2026-10-06,與補資料頁同一條規則 — 姓名 + 手機是認親與後台對人的依據,更正請找專屬顧問)
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getMe, getMyReferralCode, updateMe } from "../api/members";
import { useMemberStore } from "../store/memberStore";
import { useUIStore } from "../store/uiStore";
import TierBadge from "../components/TierBadge";
import BirthdaySelect from "../components/BirthdaySelect";
import { withNext } from "../utils/join";

export default function MemberProfile() {
  const navigate = useNavigate();
  const member = useMemberStore((s) => s.member);
  // 留過手機 + 有姓名 → 姓名鎖定(後端 update_basic_info 同一條規則,送了也會被忽略)
  const nameLocked = !!(member?.phone && member.name?.trim());
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [birthday, setBirthday] = useState("");
  const [saving, setSaving] = useState(false);
  const [referralCode, setReferralCode] = useState("");

  useEffect(() => {
    (async () => {
      const m = await getMe();
      useMemberStore.getState().setMember(m);
      setName(m.name || "");
      setEmail(m.email || "");
      setBirthday(m.birthday || "");
      try {
        setReferralCode(await getMyReferralCode());
      } catch {
        // ignore — 推薦碼載入失敗不擋資料頁
      }
    })();
  }, []);

  async function save() {
    setSaving(true);
    try {
      await updateMe(nameLocked ? { email, birthday } : { name, email, birthday });
      useUIStore.getState().showToast("已更新", "success");
      const m = await getMe();
      useMemberStore.getState().setMember(m);
    } catch {
      useUIStore.getState().showToast("更新失敗", "error");
    } finally {
      setSaving(false);
    }
  }

  if (!member) return null;
  const tier = member.tier || "general";

  return (
    <div className="min-h-screen bg-cream pb-12">
      <header className="bg-primary text-white px-5 py-6 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="text-white text-xl">‹</button>
        <h1 className="text-xl font-bold">會員資料</h1>
      </header>

      <div className="px-5 mt-5 space-y-4">
        <div className="card flex items-center justify-between">
          <div>
            <div className="text-sm text-muted">會員等級</div>
            <div className="text-lg mt-1 font-bold">
              <TierBadge tier={tier} size="lg" />
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm text-muted">總消費</div>
            <div className="text-lg mt-1 text-ink font-medium">
              NT$ {(member.total_spent || 0).toLocaleString()}
            </div>
          </div>
        </div>

        {referralCode && (
          <div className="card">
            <div className="text-sm text-muted mb-2 font-medium">我的推薦碼</div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-2xl font-mono font-bold tracking-widest text-primary">{referralCode}</span>
              <button
                onClick={() => {
                  navigator.clipboard?.writeText(referralCode);
                  useUIStore.getState().showToast("已複製推薦碼", "success");
                }}
                className="btn-secondary text-sm px-4 shrink-0"
              >
                複製
              </button>
            </div>
            <div className="text-xs text-muted mt-2">
              分享給好友,好友首單輸入即可享優惠,您也會獲得推薦回饋。
            </div>
          </div>
        )}

        <div className="card space-y-3">
          <div>
            <label className="label">姓名</label>
            <input
              className={`input ${nameLocked ? "bg-line/20" : ""}`}
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={nameLocked}
            />
          </div>
          <div>
            <label className="label">手機</label>
            {member.phone ? (
              <input className="input bg-line/20" value={member.phone} disabled />
            ) : (
              // 一鍵加入(2026-09-24)的會員還沒留手機 → 導去補資料頁,補完回這頁
              <button
                type="button"
                onClick={() => navigate(withNext("/complete-profile", "/profile"))}
                className="btn-secondary w-full text-sm"
              >
                尚未留手機,點此補齊
              </button>
            )}
            {member.phone && (
              <p className="text-xs text-muted mt-1">姓名 / 手機如需更正,請聯繫您的專屬顧問協助</p>
            )}
          </div>
          <div>
            <label className="label">Email</label>
            <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div>
            <label className="label">生日</label>
            <BirthdaySelect value={birthday} onChange={setBirthday} />
          </div>
          <button onClick={save} disabled={saving} className="btn-primary w-full mt-3">
            {saving ? "儲存中…" : "儲存"}
          </button>
        </div>

        <div className="card">
          <div className="text-sm text-muted mb-3 font-medium">會籍期間</div>
          <div className="text-sm space-y-2">
            <div>起：{member.membership_start ? String(member.membership_start).slice(0, 10) : "—"}</div>
            <div>迄：{member.membership_end ? String(member.membership_end).slice(0, 10) : "—"}</div>
          </div>
        </div>

        {(() => {
          // 寄送地址 — read-only 顯示;三欄全空時整張卡不渲染。編輯能力留下一輪
          const addrLine1 = [member.address?.city, member.address?.district].filter(Boolean).join(" ");
          const addrLine2 = member.address?.detail || "";
          if (!addrLine1 && !addrLine2) return null;
          return (
            <div className="card">
              <div className="text-sm text-muted mb-3 font-medium">寄送地址</div>
              <div className="text-sm space-y-1 text-ink">
                {addrLine1 && <div>{addrLine1}</div>}
                {addrLine2 && <div>{addrLine2}</div>}
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
}
